/* Raiman Rocks laboratory-report viewer. PDF.js is imported after report proximity or intent, never during the initial load. */
  (() => {
    const modalSelector = '[data-rr-pdf-modal]';
    const triggerSelector = '[data-rr-pdf-modal-trigger]';
    let backgroundRevision = 0;
    let backgroundQueue = Promise.resolve();
    const colorPreferenceStorageKey = 'rr-pdf-viewer-color-mode';
    const compactViewerMedia = window.matchMedia(
      '(max-width: 699px), (hover: none), (pointer: coarse)'
    );

    const readStoredColorFilterPreference = () => {
      try {
        return window.localStorage.getItem(colorPreferenceStorageKey) !== 'original';
      } catch (error) {
        return true;
      }
    };

    const storeColorFilterPreference = (isEnabled) => {
      try {
        window.localStorage.setItem(
          colorPreferenceStorageKey,
          isEnabled ? 'dark' : 'original'
        );
      } catch (error) {
        // The active viewer still changes mode when storage is blocked.
      }
    };

    const reportDownloadFilename = (laboratory, reportNumber) => {
      const prefixes = {
        'GIA': 'GIA', 'AGS': 'AGS', 'AGL': 'AGL', 'AGTA-GTC': 'AGTA-GTC',
        'AIGS': 'AIGS', 'C. Dunaigre Switzerland': 'C-Dunaigre',
        'EGL': 'EGL', 'GRS': 'GRS', 'Lotus Gemology': 'Lotus-Gemology'
      };
      const clean = value => String(value || '').trim()
        .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '-')
        .replace(/\s+/g, '-').replace(/^[.\-]+|[.\-]+$/g, '');
      const lab = String(laboratory || '').trim();
      const prefix = prefixes[lab] || clean(lab) || 'Laboratory';
      let number = String(reportNumber || '').trim();
      const labels = [...new Set([lab, prefix])].filter(Boolean).sort((a, b) => b.length - a.length);
      for (const label of labels) {
        if (number.slice(0, label.length).toLowerCase() !== label.toLowerCase()) continue;
        const rest = number.slice(label.length);
        if (/^(?:[\s:#-]+|\d)/.test(rest)) {
          number = rest.replace(/^[\s:#-]+/, '');
          break;
        }
      }
      return `${prefix}-${clean(number) || 'Report'}.pdf`;
    };

    const mountModalAtRoot = (modal) => {
      if (modal.parentElement !== document.body) {
        document.body.appendChild(modal);
      }
    };

    const findSiteHeader = () => {
      return document.querySelector(
        '.shopify-section--header, #shopify-section-header, [id^="shopify-section-sections--"][id$="__header"]'
      );
    };

    const initializeModal = (modal) => {
      mountModalAtRoot(modal);

      if (modal.dataset.rrPdfModalReady === 'true') return;

      const documentViewport = modal.querySelector('[data-rr-pdf-document]');
      const pages = modal.querySelector('[data-rr-pdf-pages]');
      const loading = modal.querySelector('[data-rr-pdf-loading]');
      const fallback = modal.querySelector('[data-rr-pdf-fallback]');
      const closeButton = modal.querySelector('[data-rr-pdf-close]');
      const printButton = modal.querySelector('[data-rr-pdf-print]');
      const printFrame = modal.querySelector('[data-rr-pdf-print-frame]');
      const downloadButton = modal.querySelector('[data-rr-pdf-download]');
      const filterToggle = modal.querySelector('[data-rr-pdf-filter-toggle]');
      const stage = modal.querySelector('[data-rr-pdf-stage]');
      const headerActions = modal.querySelector('.rr-pdf-viewer__header-actions');
      const magnifier = modal.querySelector('[data-rr-pdf-magnifier]');
      const magnifierCanvas = modal.querySelector('[data-rr-pdf-magnifier-canvas]');

      if (
        !documentViewport ||
        !pages ||
        !loading ||
        !fallback ||
        !closeButton ||
        !printButton ||
        !printFrame ||
        !downloadButton ||
        !filterToggle ||
        !stage ||
        !headerActions ||
        !magnifier ||
        !magnifierCanvas
      ) {
        return;
      }

      if (magnifier.parentElement !== document.body) {
        document.body.appendChild(magnifier);
      }

      modal.dataset.rrPdfModalReady = 'true';

      let activeTrigger = null;
      let activePdfUrl = modal.dataset.pdfUrl || '';
      let closeTimer = null;
      let resizeTimer = null;
      let resizeFrame = null;
      let pdfDocument = null;
      let loadedPdfUrl = '';
      let loadingPromise = null;
      let loadingPdfUrl = '';
      let preRenderPromise = null;
      let viewerOpen = false;
      let pageLocked = false;
      let renderGeneration = 0;
      let sessionGeneration = 0;
      let renderTasks = [];
      let colorFilterEnabled = readStoredColorFilterPreference();
      let pageTransitionFrame = null;
      let pageTransitionTimer = null;
      let pageTransitionResolve = null;
      let outgoingPages = null;
      const magnifierContext = magnifierCanvas.getContext('2d', { alpha: false });
      const magnifierMedia = window.matchMedia(
        '(hover: hover) and (pointer: fine) and (min-width: 1000px)'
      );
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const magnifierSize = 300;
      const magnification = 2;
      const pageCrossFadeDuration = 420;
      const desktopPreferredRenderScale = Math.min(
        4,
        Math.max(3, (window.devicePixelRatio || 1) * 2)
      );
      const mobilePreferredRenderScale = 5;
      const maximumCanvasPixels = 16000000;
      const maximumTotalCanvasPixels = 24000000;
      const minimumPageSidePadding = 10;
      const desktopActionClearance = 74;
      const actionLabelClearance = 126;
      const accentSaturationStart = 0.12;
      const accentSaturationEnd = 0.25;
      const accentChromaStart = 16;
      const accentChromaEnd = 40;
      const accentLuminanceFadeStart = 170;
      const accentLuminanceFadeEnd = 220;
      const neutralShadowPoint = 0.025;
      const neutralHighlightPoint = 0.72;
      const neutralWhiteLevel = 238;

      const smoothstep = (start, end, value) => {
        const normalized = Math.min(1, Math.max(0, (value - start) / (end - start)));
        return normalized * normalized * (3 - 2 * normalized);
      };

      const getRenderOutputScale = (width, height, pageCount) => {
        const cssPixelCount = Math.max(1, width * height);
        const pagePixelBudget = Math.min(
          maximumCanvasPixels,
          maximumTotalCanvasPixels / Math.max(1, pageCount)
        );
        const budgetScale = Math.sqrt(pagePixelBudget / cssPixelCount);
        const preferredRenderScale = compactViewerMedia.matches
          ? mobilePreferredRenderScale
          : desktopPreferredRenderScale;

        return Math.max(
          Math.min(window.devicePixelRatio || 1, 2),
          Math.min(preferredRenderScale, budgetScale)
        );
      };

      const updateActionLabelVisibility = () => {
        const actionBounds = headerActions.getBoundingClientRect();
        const labelsWouldClip =
          compactViewerMedia.matches ||
          actionBounds.right + actionLabelClearance >
            window.innerWidth - minimumPageSidePadding;

        modal.classList.toggle(
          'rr-pdf-viewer--hide-action-labels',
          labelsWouldClip
        );
      };

      // C. Dunaigre alone uses raster photo protection; other labs retain legacy remapping.
      const getPdfPhotoRegions = async (page, viewport, outputScale, width, height) => {
        const laboratory = (modal.dataset.pdfLaboratory || '').toLowerCase().replace(/[^a-z]/g, '');
        if (laboratory !== 'cdunaigre' && laboratory !== 'cdunaigreswitzerland') return [];
        const { OPS } = await loadPdfLibrary();
        const operations = await page.getOperatorList();
        const imageOps = new Set(Object.entries(OPS)
          .filter(([name]) => /^paint.*Image/.test(name) && !/Mask/.test(name))
          .map(([, value]) => value));
        const regions = [];
        const stack = [];
        // Scale output coordinates, rather than PDF coordinates, for rotated pages.
        let matrix = new DOMMatrix([outputScale, 0, 0, outputScale, 0, 0])
          .multiply(new DOMMatrix(viewport.transform));
        for (let i = 0; i < operations.fnArray.length; i += 1) {
          const operation = operations.fnArray[i];
          const args = operations.argsArray[i];
          if (operation === OPS.save) stack.push(matrix);
          else if (operation === OPS.restore) matrix = stack.pop() || matrix;
          else if (operation === OPS.transform) matrix = matrix.multiply(new DOMMatrix(args));
          else if (operation === OPS.paintFormXObjectBegin) {
            stack.push(matrix);
            if (args[0]) matrix = matrix.multiply(new DOMMatrix(args[0]));
          } else if (operation === OPS.paintFormXObjectEnd) matrix = stack.pop() || matrix;
          else if (imageOps.has(operation)) {
            if (operation !== OPS.paintImageXObject && operation !== OPS.paintInlineImageXObject &&
                operation !== OPS.paintJpegXObject) return [[0, 0, width, height]];
            const points = [[0, 0], [1, 0], [0, 1], [1, 1]]
              .map(([x, y]) => matrix.transformPoint({ x, y }));
            regions.push([
              Math.max(0, Math.floor(Math.min(...points.map(p => p.x))) - 1),
              Math.max(0, Math.floor(Math.min(...points.map(p => p.y))) - 1),
              Math.min(width, Math.ceil(Math.max(...points.map(p => p.x))) + 1),
              Math.min(height, Math.ceil(Math.max(...points.map(p => p.y))) + 1)
            ]);
          }
        }
        // C. Dunaigre scans use a consistent first-page photo-card layout.
        // Structured PDFs keep their actual image bounds; unknown scans stay original.
        const fullPageScan = regions.some(([left, top, right, bottom]) =>
          (right - left) >= width * 0.9 && (bottom - top) >= height * 0.9);
        if (page.pageNumber === 1 && fullPageScan &&
            (laboratory === 'cdunaigre' || laboratory === 'cdunaigreswitzerland')) {
          // Buffered search zone, not a white rectangular overlay. Connected
          // exterior paper is removed while enclosed photo highlights are retained.
          const box = width > height
            ? [0.62, 0.25, 0.89, 0.59]
            : [0.25, 0.11, 0.59, 0.38];
          return [[box[0] * width, box[1] * height, box[2] * width, box[3] * height, true]];
        }
        return regions;
      };

      const remapPdfPixels = (context, width, height, photoRegions = []) => {
        let imageData;

        try {
          imageData = context.getImageData(0, 0, width, height);
        } catch (error) {
          return;
        }

        const pixels = imageData.data;
        const originalPixels = new Uint8ClampedArray(pixels);
        const protectedPixels = new Uint8Array(width * height);
        for (const [left, top, right, bottom, removeExteriorPaper] of photoRegions) {
          const x0 = Math.max(0, Math.ceil(left));
          const y0 = Math.max(0, Math.ceil(top));
          const x1 = Math.min(width - 1, Math.floor(right));
          const y1 = Math.min(height - 1, Math.floor(bottom));
          if (x0 > x1 || y0 > y1) continue;
          for (let y = y0; y <= y1; y += 1) {
            protectedPixels.fill(1, y * width + x0, y * width + x1 + 1);
          }
          if (!removeExteriorPaper) continue;
          // Only the connected paper outside the photographed stone is remapped.
          // Enclosed white facets retain their original pixels.
          // Allow the paper's photographed color cast without loosening the
          // white-scan threshold for diamond/metal highlights in other reports.
          const paperSamples = [];
          const samplePaper = (x, y) => {
            const offset = (y * width + x) * 4;
            const channels = [pixels[offset], pixels[offset + 1], pixels[offset + 2]];
            paperSamples.push([Math.min(...channels), Math.max(...channels) - Math.min(...channels)]);
          };
          const sampleStep = Math.max(1, Math.floor((x1 - x0 + y1 - y0) / 128));
          for (let x = x0; x <= x1; x += sampleStep) { samplePaper(x, y0); samplePaper(x, y1); }
          for (let y = y0; y <= y1; y += sampleStep) { samplePaper(x0, y); samplePaper(x1, y); }
          const median = values => values.sort((a, b) => a - b)[Math.floor(values.length / 2)];
          const paperFloor = Math.max(140, Math.min(190, median(paperSamples.map(sample => sample[0])) - 45));
          const paperChroma = Math.max(24, Math.min(65, median(paperSamples.map(sample => sample[1])) + 20));
          const queue = [];
          const visitPaper = (x, y) => {
            if (x < x0 || x > x1 || y < y0 || y > y1) return;
            const pixel = y * width + x;
            if (!protectedPixels[pixel]) return;
            const offset = pixel * 4;
            const channels = [pixels[offset], pixels[offset + 1], pixels[offset + 2]];
            if (Math.min(...channels) < paperFloor || Math.max(...channels) - Math.min(...channels) > paperChroma) return;
            protectedPixels[pixel] = 0;
            queue.push(pixel);
          };
          for (let x = x0; x <= x1; x += 1) { visitPaper(x, y0); visitPaper(x, y1); }
          for (let y = y0; y <= y1; y += 1) { visitPaper(x0, y); visitPaper(x1, y); }
          for (let i = 0; i < queue.length; i += 1) {
            const x = queue[i] % width;
            const y = Math.floor(queue[i] / width);
            visitPaper(x - 1, y); visitPaper(x + 1, y);
            visitPaper(x, y - 1); visitPaper(x, y + 1);
          }
        }
        const edgeRadius = Math.min(4, Math.max(1, Math.ceil(width / 1200)));
        const edgeDirections = [[-1, 0], [1, 0], [0, -1], [0, 1], [-1, -1], [1, -1], [-1, 1], [1, 1]];

        for (let index = 0; index < pixels.length; index += 4) {
          if (protectedPixels[index / 4]) continue;
          const red = pixels[index];
          const green = pixels[index + 1];
          const blue = pixels[index + 2];
          const maximumChannel = Math.max(red, green, blue);
          const minimumChannel = Math.min(red, green, blue);
          const chroma = maximumChannel - minimumChannel;
          const saturation = maximumChannel === 0 ? 0 : chroma / maximumChannel;

          const luminance =
            red * 0.2126 +
            green * 0.7152 +
            blue * 0.0722;
          const saturationWeight = smoothstep(
            accentSaturationStart,
            accentSaturationEnd,
            saturation
          );
          const chromaWeight =
            smoothstep(accentChromaStart, accentChromaEnd, chroma) *
            (1 - smoothstep(
              accentLuminanceFadeStart,
              accentLuminanceFadeEnd,
              luminance
            ));
          const accentWeight = Math.max(saturationWeight, chromaWeight) *
            smoothstep(4, 20, chroma);

          // Scanned color edges contain white paper mixed into the ink. Use nearby
          // solid colored ink to composite those edge pixels onto black instead of
          // turning their pale paper component into a white outline.
          if (red > green + 12 && red > blue + 8 && chroma >= 8 && luminance > 100 && luminance < 248 && (accentWeight < 0.98 || luminance > 180)) {
            const pixelNumber = index / 4;
            const x = pixelNumber % width;
            const y = Math.floor(pixelNumber / width);
            let edgeInk = null;
            for (let distance = 1; distance <= edgeRadius && !edgeInk; distance += 1) {
              for (const [dx, dy] of edgeDirections) {
                const nx = x + dx * distance;
                const ny = y + dy * distance;
                if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
                const neighbor = (ny * width + nx) * 4;
                const nr = originalPixels[neighbor];
                const ng = originalPixels[neighbor + 1];
                const nb = originalPixels[neighbor + 2];
                const high = Math.max(nr, ng, nb);
                const spread = high - Math.min(nr, ng, nb);
                const inkLuminance = nr * 0.2126 + ng * 0.7152 + nb * 0.0722;
                if (nr > ng + 35 && nr > nb + 25 && spread >= 50 && spread / (high || 1) >= 0.4 &&
                    inkLuminance < 160 && luminance > inkLuminance + 20) {
                  edgeInk = [nr, ng, nb, inkLuminance];
                  break;
                }
              }
            }
            if (edgeInk) {
              const coverage = Math.min(1, (255 - luminance) / (255 - edgeInk[3]));
              pixels[index] = Math.round(edgeInk[0] * coverage);
              pixels[index + 1] = Math.round(edgeInk[1] * coverage);
              pixels[index + 2] = Math.round(edgeInk[2] * coverage);
              continue;
            }
          }

          const invertedStrength = (255 - luminance) / 255;
          const remappedLuminance = Math.round(
            neutralWhiteLevel *
            Math.pow(smoothstep(
              neutralShadowPoint,
              neutralHighlightPoint,
              invertedStrength
            ), 0.45)
          );

          // Preserve faint scanned ink; lift dark accents without inverting their hue.
          const accentLift = Math.max(0, (120 - luminance) / (255 - luminance || 1)) *
            (1 - smoothstep(0.15, 0.35, saturation));
          const readableRed = red + (255 - red) * accentLift;
          const readableGreen = green + (255 - green) * accentLift;
          const readableBlue = blue + (255 - blue) * accentLift;

          pixels[index] = Math.round(
            remappedLuminance * (1 - accentWeight) +
            readableRed * accentWeight
          );
          pixels[index + 1] = Math.round(
            remappedLuminance * (1 - accentWeight) +
            readableGreen * accentWeight
          );
          pixels[index + 2] = Math.round(
            remappedLuminance * (1 - accentWeight) +
            readableBlue * accentWeight
          );
        }

        context.putImageData(imageData, 0, 0);
      };

      const syncFilterToggle = () => {
        const isEnabled = colorFilterEnabled;

        filterToggle.setAttribute('aria-pressed', isEnabled ? 'true' : 'false');
        filterToggle.setAttribute(
          'aria-label',
          isEnabled
            ? 'Show original report colors'
            : 'Enable dark report colors'
        );
        filterToggle.dataset.rrPdfActionLabel = isEnabled
          ? 'ORIGINAL'
          : 'DARK MODE';
      };

      syncFilterToggle();

      let magnifierFrame = null;
      let pendingMagnifier = null;
      let magnifierSource = null;

      const hideMagnifier = () => {
        if (magnifierFrame !== null) {
          window.cancelAnimationFrame(magnifierFrame);
          magnifierFrame = null;
        }
        pendingMagnifier = null;
        magnifierSource = null;
        magnifier.classList.remove('is-visible');
      };

      const isViewportZoomed = () => {
        return (window.visualViewport?.scale || 1) > 1.01;
      };

      const drawMagnifier = () => {
        magnifierFrame = null;
        const event = pendingMagnifier;
        pendingMagnifier = null;
        if (!event) return;
        if (!magnifierMedia.matches || isViewportZoomed()) {
          hideMagnifier();
          return;
        }

        const sourceCanvas = event.sourceCanvas;
        if (!sourceCanvas) {
          hideMagnifier();
          return;
        }

        const sourceRect = sourceCanvas.getBoundingClientRect();
        const sourceScaleX = sourceCanvas.width / sourceRect.width;
        const sourceScaleY = sourceCanvas.height / sourceRect.height;
        const sourceCssSize = magnifierSize / magnification;
        const sourceWidth = sourceCssSize * sourceScaleX;
        const sourceHeight = sourceCssSize * sourceScaleY;
        const sourceX = Math.min(
          Math.max(0, (event.clientX - sourceRect.left) * sourceScaleX - sourceWidth / 2),
          Math.max(0, sourceCanvas.width - sourceWidth)
        );
        const sourceY = Math.min(
          Math.max(0, (event.clientY - sourceRect.top) * sourceScaleY - sourceHeight / 2),
          Math.max(0, sourceCanvas.height - sourceHeight)
        );
        // Copy a report surface once per activation. Pointer movement then only
        // translates that surface inside the clipped lens, avoiding Safari's
        // repeated canvas-to-canvas sampling and upload path.
        if (
          magnifierSource !== sourceCanvas ||
          magnifierCanvas.width !== sourceCanvas.width ||
          magnifierCanvas.height !== sourceCanvas.height
        ) {
          magnifierCanvas.width = sourceCanvas.width;
          magnifierCanvas.height = sourceCanvas.height;
          magnifierContext.drawImage(sourceCanvas, 0, 0);
          magnifierSource = sourceCanvas;
        }
        magnifierCanvas.style.width = `${sourceRect.width * magnification}px`;
        magnifierCanvas.style.height = `${sourceRect.height * magnification}px`;
        const lensX = -sourceX / sourceScaleX * magnification;
        const lensY = -sourceY / sourceScaleY * magnification;
        magnifierCanvas.style.transform = `translate3d(${lensX}px, ${lensY}px, 0)`;

        magnifier.style.translate = `${event.clientX}px ${event.clientY}px`;
        magnifier.classList.add('is-visible');
      };

      const moveMagnifier = (event) => {
        const sourceCanvas = event.target.closest('.rr-pdf-viewer__page canvas');
        if (!sourceCanvas || !magnifierMedia.matches || isViewportZoomed()) {
          hideMagnifier();
          return;
        }
        pendingMagnifier = {
          sourceCanvas,
          clientX: event.clientX,
          clientY: event.clientY
        };
        if (magnifierFrame === null) {
          magnifierFrame = window.requestAnimationFrame(drawMagnifier);
        }
      };

      const setHeaderOffset = () => {
        const siteHeader = findSiteHeader();
        const rect = siteHeader?.getBoundingClientRect();
        const fallbackHeight = window.innerWidth < 700 ? 70 : 82;
        const headerHeight = rect && rect.height > 0
          ? Math.ceil(rect.height)
          : fallbackHeight;

        modal.style.setProperty('--rr-pdf-site-header-height', `${headerHeight}px`);
      };

      const getRenderLayoutKey = () => {
        const headerHeight = modal.style.getPropertyValue('--rr-pdf-site-header-height');

        return [
          activePdfUrl,
          window.innerWidth,
          window.innerHeight,
          headerHeight,
          colorFilterEnabled ? 'filtered' : 'original'
        ].join(':');
      };

      const loadPdfLibrary = async () => {
        if (!window.rrPdfLibraryPromise) {
          const moduleUrl = new URL(
            modal.dataset.pdfModuleUrl,
            window.location.href
          ).href;
          const workerUrl = new URL(
            modal.dataset.pdfWorkerUrl,
            window.location.href
          ).href;

          window.rrPdfLibraryPromise = import(moduleUrl).then((pdfLibrary) => {
            pdfLibrary.GlobalWorkerOptions.workerSrc = workerUrl;
            return pdfLibrary;
          });
        }

        return window.rrPdfLibraryPromise;
      };

      const lockPage = () => {
        if (pageLocked) return;
        pageLocked = true;

        document.documentElement.classList.add('rr-pdf-viewer-is-open');
        document.body.classList.add('rr-pdf-viewer-is-open');
        document.body.classList.add('rr-pdf-page-content-transitioning');
        document.dispatchEvent(new Event('rr:pdf-viewer-state'));
      };

      const unlockPage = () => {
        document.documentElement.classList.remove('rr-pdf-viewer-is-open');
        document.body.classList.remove('rr-pdf-viewer-is-open');
        document.body.classList.remove('rr-pdf-page-content-hidden');
        document.body.classList.remove('rr-pdf-page-content-transitioning');
        pageLocked = false;
        document.dispatchEvent(new Event('rr:pdf-viewer-state'));
      };

      const finishPageTransition = () => {
        if (pageTransitionFrame !== null) {
          window.cancelAnimationFrame(pageTransitionFrame);
          pageTransitionFrame = null;
        }

        window.clearTimeout(pageTransitionTimer);
        pageTransitionTimer = null;
        outgoingPages?.remove();
        outgoingPages = null;
        pages.classList.remove(
          'rr-pdf-viewer__pages--incoming',
          'is-visible'
        );

        const resolveTransition = pageTransitionResolve;
        pageTransitionResolve = null;
        resolveTransition?.();
      };

      const replaceRenderedPages = (fragment, crossFade) => {
        finishPageTransition();

        if (
          !crossFade ||
          reduceMotion.matches ||
          pages.childElementCount === 0
        ) {
          pages.replaceChildren(fragment);
          return Promise.resolve();
        }

        outgoingPages = pages.cloneNode(false);
        outgoingPages.removeAttribute('data-rr-pdf-pages');
        outgoingPages.classList.add('rr-pdf-viewer__pages--outgoing');

        while (pages.firstChild) {
          outgoingPages.appendChild(pages.firstChild);
        }

        pages.before(outgoingPages);
        pages.appendChild(fragment);
        pages.classList.add('rr-pdf-viewer__pages--incoming');
        void pages.offsetWidth;

        return new Promise((resolve) => {
          pageTransitionResolve = resolve;
          pageTransitionFrame = window.requestAnimationFrame(() => {
            pageTransitionFrame = null;
            pages.classList.add('is-visible');
            outgoingPages?.classList.add('is-hidden');
          });
          pageTransitionTimer = window.setTimeout(
            finishPageTransition,
            pageCrossFadeDuration
          );
        });
      };

      const cancelRenders = () => {
        renderGeneration += 1;
        renderTasks.forEach((task) => task.cancel?.());
        renderTasks = [];
        finishPageTransition();
      };

      const ensurePdfDocument = async () => {
        if (pdfDocument && loadedPdfUrl === activePdfUrl) {
          return pdfDocument;
        }

        if (loadingPromise && loadingPdfUrl === activePdfUrl) {
          return loadingPromise;
        }

        if (pdfDocument) {
          await pdfDocument.destroy();
          pdfDocument = null;
        }

        loadingPdfUrl = activePdfUrl;
        loadingPromise = (async () => {
          const pdfLibrary = await loadPdfLibrary();
          const response = await fetch(activePdfUrl, { credentials: 'same-origin' });

          if (!response.ok) {
            throw new Error(`Unable to load PDF: ${response.status}`);
          }

          const data = await response.arrayBuffer();
          const loadingTask = pdfLibrary.getDocument({ data });
          const loadedDocument = await loadingTask.promise;

          pdfDocument = loadedDocument;
          loadedPdfUrl = activePdfUrl;
          return loadedDocument;
        })();

        try {
          return await loadingPromise;
        } finally {
          loadingPromise = null;
          loadingPdfUrl = '';
        }
      };

      // Keep the existing raster visible and fit it every animation frame while
      // resizing. PDF.js only regenerates sharp pixels after the drag settles.
      const fitDisplayedPages = () => {
        const clearance = compactViewerMedia.matches ? 0 : minimumPageSidePadding * 2 + desktopActionClearance * 2;
        const availableWidth = Math.max(80, documentViewport.clientWidth - clearance);
        const availableHeight = Math.max(80, documentViewport.clientHeight);
        let widest = 0;
        pages.querySelectorAll('canvas').forEach(canvas => {
          const naturalWidth = Number(canvas.dataset.pageWidth) || canvas.width;
          const naturalHeight = Number(canvas.dataset.pageHeight) || canvas.height;
          const scale = Math.min(availableWidth / naturalWidth, availableHeight / naturalHeight);
          const width = Math.floor(naturalWidth * scale);
          canvas.style.width = `${width}px`;
          canvas.style.height = `${Math.floor(naturalHeight * scale)}px`;
          widest = Math.max(widest, width);
        });
        if (widest) stage.style.setProperty('--rr-pdf-page-half-width', `${Math.floor(widest / 2)}px`);
        updateActionLabelVisibility();
      };

      const renderPdf = async ({ crossFade = false } = {}) => {
        if (!pdfDocument || modal.hidden) return;

        const generation = ++renderGeneration;
        const previousCenter = documentViewport.scrollWidth > documentViewport.clientWidth
          ? (documentViewport.scrollLeft + documentViewport.clientWidth / 2) /
            documentViewport.scrollWidth
          : 0.5;
        const horizontalClearance = compactViewerMedia.matches
          ? 0
          : minimumPageSidePadding * 2 + desktopActionClearance * 2;
        const availableWidth = Math.max(
          80,
          documentViewport.clientWidth - horizontalClearance
        );
        const availableHeight = Math.max(
          80,
          documentViewport.clientHeight
        );
        const fragment = document.createDocumentFragment();
        const nextRenderTasks = [];
        let renderedPageWidth = 0;

        documentViewport.setAttribute('aria-busy', 'true');

        for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
          const page = await pdfDocument.getPage(pageNumber);
          const naturalViewport = page.getViewport({ scale: 1 });
          const fitScale = Math.min(
            availableWidth / naturalViewport.width,
            availableHeight / naturalViewport.height
          );
          const viewport = page.getViewport({ scale: fitScale });
          const outputScale = getRenderOutputScale(
            viewport.width,
            viewport.height,
            pdfDocument.numPages
          );
          const pageElement = document.createElement('div');
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d', { alpha: false });

          pageElement.className = 'rr-pdf-viewer__page';
          pageElement.setAttribute('aria-label', `Page ${pageNumber} of ${pdfDocument.numPages}`);

          canvas.dataset.pageWidth = naturalViewport.width;
          canvas.dataset.pageHeight = naturalViewport.height;
          canvas.width = Math.max(1, Math.floor(viewport.width * outputScale));
          canvas.height = Math.max(1, Math.floor(viewport.height * outputScale));
          canvas.style.width = `${Math.floor(viewport.width)}px`;
          canvas.style.height = `${Math.floor(viewport.height)}px`;
          renderedPageWidth = Math.max(renderedPageWidth, viewport.width);

          pageElement.appendChild(canvas);
          fragment.appendChild(pageElement);

          const renderTask = page.render({
            canvasContext: context,
            viewport,
            transform: outputScale === 1
              ? null
              : [outputScale, 0, 0, outputScale, 0, 0],
            background: 'rgb(255,255,255)'
          });

          nextRenderTasks.push(renderTask);
          renderTasks = nextRenderTasks;

          try {
            await renderTask.promise;
          } catch (error) {
            if (error?.name === 'RenderingCancelledException') return;
            throw error;
          }

          if (generation !== renderGeneration || modal.hidden) return;
          if (colorFilterEnabled) {
            let photoRegions;
            try {
              photoRegions = await getPdfPhotoRegions(page, viewport, outputScale, canvas.width, canvas.height);
            } catch (error) {
              // Never risk recoloring photographs if image bounds cannot be read.
              photoRegions = [[0, 0, canvas.width, canvas.height]];
            }
            if (generation !== renderGeneration || modal.hidden) return;
            remapPdfPixels(context, canvas.width, canvas.height, photoRegions);
          }
        }

        if (generation !== renderGeneration || modal.hidden) return;

        await replaceRenderedPages(fragment, crossFade);
        if (generation !== renderGeneration || modal.hidden) return;

        modal.dataset.rrPdfRenderKey = getRenderLayoutKey();
        stage.style.setProperty(
          '--rr-pdf-page-half-width',
          `${Math.floor(renderedPageWidth / 2)}px`
        );
        window.requestAnimationFrame(updateActionLabelVisibility);
        modal.classList.add('has-pdf');
        loading.hidden = true;
        fallback.hidden = true;
        renderTasks = [];

        window.requestAnimationFrame(() => {
          const targetCenter = documentViewport.scrollWidth * previousCenter;
          documentViewport.scrollLeft = Math.max(
            0,
            targetCenter - documentViewport.clientWidth / 2
          );
          documentViewport.removeAttribute('aria-busy');
        });
      };

      const showFallback = () => {
        cancelRenders();
        loading.hidden = true;
        fallback.hidden = false;
        documentViewport.removeAttribute('aria-busy');
        window.open(activePdfUrl, '_blank', 'noopener,noreferrer');
      };

      const loadAndRender = async (session) => {
        try {
          if (preRenderPromise) await preRenderPromise;
          await ensurePdfDocument();
          if (session !== sessionGeneration || modal.hidden) return;
          await renderPdf();
        } catch (error) {
          if (session !== sessionGeneration || modal.hidden) return;
          showFallback();
        }
      };

      const preRenderPdf = (mode = readStoredColorFilterPreference()) => {
        if (viewerOpen) return Promise.resolve();
        if (!preRenderPromise) colorFilterEnabled = mode;
        if (!activePdfUrl) {
          return Promise.resolve();
        }

        if (
          loadedPdfUrl === activePdfUrl &&
          pages.childElementCount > 0 &&
          modal.dataset.rrPdfRenderKey === getRenderLayoutKey()
        ) {
          return Promise.resolve();
        }

        if (preRenderPromise) {
          return preRenderPromise;
        }

        preRenderPromise = (async () => {
          try {
            setHeaderOffset();
            modal.classList.remove('is-active');
            modal.setAttribute('aria-hidden', 'true');
            modal.hidden = false;

            await new Promise((resolve) => {
              window.requestAnimationFrame(resolve);
            });

            await ensurePdfDocument();
            await renderPdf();
          } catch (error) {
            modal.dataset.rrPdfRenderKey = '';
          } finally {
            if (!viewerOpen) {
              modal.hidden = true;
            }

            preRenderPromise = null;
          }
        })();

        return preRenderPromise;
      };

      const closeModal = ({ returnFocus = true, scrollToTop = false, drawerHandoff = false } = {}) => {
        if (modal.hidden) return;

        viewerOpen = false;
        backgroundRevision += 1;
        hideMagnifier();
        sessionGeneration += 1;
        cancelRenders();
        document.body.classList.remove('rr-pdf-page-content-hidden');
        modal.classList.remove('is-active');
        modal.setAttribute('aria-hidden', 'true');
        document.dispatchEvent(new Event('rr:pdf-viewer-state'));
        window.clearTimeout(closeTimer);

        if (drawerHandoff) {
          unlockPage();
          modal.style.zIndex = '9';
          modal.style.pointerEvents = 'none';
        }

        const onCloseTransitionEnd = (event) => {
          if (event.target === modal && event.propertyName === 'opacity') finishClose();
        };
        const finishClose = () => {
          modal.removeEventListener('transitionend', onCloseTransitionEnd);
          window.clearTimeout(closeTimer);
          if (viewerOpen) return;
          modal.hidden = true;
          modal.style.removeProperty('z-index');
          modal.style.removeProperty('pointer-events');
          unlockPage();
          if (scrollToTop) {
            window.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'instant' : 'smooth' });
            const heading = document.querySelector('.shopify-section--main-product h1.product-title');
            if (heading) {
              const addedTabIndex = !heading.hasAttribute('tabindex');
              if (addedTabIndex) heading.setAttribute('tabindex', '-1');
              heading.focus({ preventScroll: true });
              if (addedTabIndex) heading.addEventListener('blur', () => heading.removeAttribute('tabindex'), { once: true });
            }
          }

          if (activeTrigger && returnFocus) {
            const trigger = activeTrigger;

            trigger.classList.add('rr-pdf-returned-focus');
            trigger.addEventListener(
              'blur',
              () => trigger.classList.remove('rr-pdf-returned-focus'),
              { once: true }
            );
            trigger.focus({ preventScroll: true });
          }

          activeTrigger = null;
        };
        if (reduceMotion.matches) finishClose();
        else if (drawerHandoff) {
          modal.addEventListener('transitionend', onCloseTransitionEnd);
          closeTimer = window.setTimeout(finishClose, 1000);
        } else closeTimer = window.setTimeout(finishClose, 450);
      };

      const openModal = (trigger, pdfUrl) => {
        trigger.classList.remove('rr-pdf-returned-focus');
        activeTrigger = trigger;
        activePdfUrl = pdfUrl || modal.dataset.pdfUrl || '';

        if (!activePdfUrl) return;

        backgroundRevision += 1;
        if (preRenderPromise) cancelRenders();
        colorFilterEnabled = readStoredColorFilterPreference();
        syncFilterToggle();

        window.clearTimeout(closeTimer);
        modal.style.removeProperty('z-index');
        modal.style.removeProperty('pointer-events');
        syncReportNavigation();
        setHeaderOffset();
        documentViewport.scrollTop = 0;
        viewerOpen = true;

        const hasPreRenderedCanvas =
          loadedPdfUrl === activePdfUrl &&
          pages.childElementCount > 0 &&
          modal.dataset.rrPdfRenderKey === getRenderLayoutKey();

        if (hasPreRenderedCanvas) {
          modal.classList.add('has-pdf');
          loading.hidden = true;
          fallback.hidden = true;
        } else {
          modal.classList.remove('has-pdf');
          loading.hidden = false;
          fallback.hidden = true;
        }

        downloadButton.href = activePdfUrl;
        downloadButton.download = reportDownloadFilename(modal.dataset.pdfLaboratory, modal.dataset.pdfReportNumber);
        modal.hidden = false;
        modal.setAttribute('aria-hidden', 'false');
        lockPage();

        const session = ++sessionGeneration;

        window.requestAnimationFrame(() => {
          document.body.classList.add('rr-pdf-page-content-hidden');
          modal.classList.add('is-active');
          closeButton.focus();
        });

        if (!hasPreRenderedCanvas) {
          loadAndRender(session).then(() => {
            if (viewerOpen && session === sessionGeneration) preloadOtherReports();
          });
        } else {
          preloadOtherReports();
        }
      };

      modal.rrOpenPdf = openModal;
      modal.rrPreloadPdf = async (mode) => {
        if (preRenderPromise) await preRenderPromise;
        if (viewerOpen) return;
        syncReportNavigation();
        await preRenderPdf(mode);
      };
      const preloadOtherReports = () => {
        if (!viewerOpen) return;
        const revision = ++backgroundRevision;
        const mode = colorFilterEnabled;
        for (const target of reportGroup()) {
          if (target === modal) continue;
          backgroundQueue = backgroundQueue.catch(() => {}).then(async () => {
            if (revision !== backgroundRevision || !viewerOpen || mode !== colorFilterEnabled) return;
            // Yield between reports so active-viewer controls remain responsive.
            await new Promise(resolve => window.setTimeout(resolve, 0));
            if (revision !== backgroundRevision || !viewerOpen) return;
            initializeModal(target);
            await target.rrPreloadPdf(mode);
          });
        }
        backgroundQueue = backgroundQueue.catch(() => {});
      };

      const reportNav = modal.querySelector('[data-rr-report-nav]');
      const reportPrev = modal.querySelector('[data-rr-report-prev]');
      const reportNext = modal.querySelector('[data-rr-report-next]');
      const reportLabel = modal.querySelector('[data-rr-report-label]');
      const reportGroup = () => Array.from(document.querySelectorAll(modalSelector)).filter(item =>
        modal.dataset.reportGroup && item.dataset.reportGroup === modal.dataset.reportGroup && item.dataset.pdfUrl);
      const syncReportNavigation = () => {
        if (!reportNav) return;
        const reports = reportGroup();
        const index = reports.indexOf(modal);
        const multiple = reports.length > 1;
        reportNav.hidden = !multiple;
        modal.classList.toggle('has-report-navigation', multiple);
        reportPrev.disabled = index <= 0;
        reportNext.disabled = index < 0 || index >= reports.length - 1;
        reportLabel.textContent = `${index + 1} of ${reports.length}`;
        reportLabel.setAttribute('aria-label', `Report ${index + 1} of ${reports.length}`);
      };
      const switchReport = direction => {
        const reports = reportGroup();
        const target = reports[reports.indexOf(modal) + direction];
        if (!target) return;
        initializeModal(target);
        const returnTrigger = activeTrigger;
        viewerOpen = false;
        backgroundRevision += 1;
        hideMagnifier();
        sessionGeneration += 1;
        cancelRenders();
        window.clearTimeout(closeTimer);
        modal.hidden = true;
        modal.classList.remove('is-active');
        modal.setAttribute('aria-hidden', 'true');
        // Transfer the existing page lock and return-focus target without flashing the product.
        pageLocked = false;
        activeTrigger = null;
        target.rrOpenPdf(returnTrigger, target.dataset.pdfUrl);
        window.requestAnimationFrame(() => {
          const preferred = target.querySelector(direction > 0 ? '[data-rr-report-next]' : '[data-rr-report-prev]');
          const fallback = target.querySelector(direction > 0 ? '[data-rr-report-prev]' : '[data-rr-report-next]');
          (preferred?.disabled ? fallback : preferred)?.focus({ preventScroll: true });
        });
      };
      reportPrev?.addEventListener('click', () => switchReport(-1));
      reportNext?.addEventListener('click', () => switchReport(1));
      syncReportNavigation();

      const reportTriggers = Array.from(
        document.querySelectorAll(triggerSelector)
      ).filter((trigger) => trigger.getAttribute('aria-controls') === modal.id);
      const preRenderForTrigger = (trigger) => {
        const triggerPdfUrl = trigger?.dataset.rrPdfUrl || modal.dataset.pdfUrl || '';

        if (triggerPdfUrl) {
          activePdfUrl = triggerPdfUrl;
        }

        preRenderPdf();
      };
      const handlePreRenderIntent = (event) => {
        preRenderForTrigger(event.currentTarget);
      };
      const preRenderAfterInitialLoad = (trigger) => {
        const schedulePreRender = () => {
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(() => preRenderForTrigger(trigger), {
              timeout: 2000
            });
          } else {
            window.setTimeout(() => preRenderForTrigger(trigger), 0);
          }
        };

        if (document.readyState === 'complete') {
          schedulePreRender();
        } else {
          window.addEventListener('load', schedulePreRender, { once: true });
        }
      };

      reportTriggers.forEach((trigger) => {
        trigger.addEventListener('pointerenter', handlePreRenderIntent, {
          once: true,
          passive: true
        });
        trigger.addEventListener('focus', handlePreRenderIntent, { once: true });
        trigger.addEventListener('touchstart', handlePreRenderIntent, {
          once: true,
          passive: true
        });
      });

      if ('IntersectionObserver' in window && reportTriggers.length > 0) {
        const preRenderObserver = new IntersectionObserver((entries) => {
          const visibleEntry = entries.find((entry) => entry.isIntersecting);

          if (!visibleEntry) return;

          preRenderObserver.disconnect();
          preRenderAfterInitialLoad(visibleEntry.target);
        }, {
          rootMargin: '300px 0px'
        });

        reportTriggers.forEach((trigger) => preRenderObserver.observe(trigger));
      }

      closeButton.addEventListener('click', closeModal);

      document.addEventListener('dialog:before-show', (event) => {
        if (!modal.isConnected || !viewerOpen) return;
        if (event.target.matches?.('header-sidebar#sidebar-menu, cart-drawer, country-drawer')) {
          closeModal({ returnFocus: false, drawerHandoff: true });
        }
      }, true);

      document.addEventListener('click', (event) => {
        if (!viewerOpen) return;

        if (event.composedPath().some(node => node instanceof Element && node.matches('.rr-product-header__top'))) {
          event.preventDefault();
          event.stopPropagation();
          closeModal({ returnFocus: false, scrollToTop: true });
          return;
        }
        const headerControlSelector = [
          '.header__search-open-button',
          'shopify-account'
        ].join(',');
        const clickedHeaderControl = event.composedPath().some((node) => {
          return node instanceof Element && node.matches(headerControlSelector);
        });

        if (clickedHeaderControl) {
          closeModal({ returnFocus: false });
        }
      }, true);

      pages.addEventListener('pointermove', moveMagnifier);
      pages.addEventListener('pointerleave', hideMagnifier);

      stage.addEventListener('click', (event) => {
        if (event.target.closest('.rr-pdf-viewer__page')) return;
        if (event.target.closest('.rr-pdf-viewer__header-actions')) return;
        closeModal();
      });

      modal.addEventListener('wheel', (event) => {
        if (event.ctrlKey || isViewportZoomed()) {
          hideMagnifier();
          return;
        }

        event.preventDefault();
      }, { passive: false });

      modal.addEventListener('touchmove', (event) => {
        const isPinching = event.touches.length >= 2;
        const canRepositionZoomedReport =
          compactViewerMedia.matches && isViewportZoomed();

        if (isPinching || canRepositionZoomedReport) {
          hideMagnifier();
          return;
        }

        event.preventDefault();
      }, { passive: false });

      const handleViewportZoom = () => {
        if (!viewerOpen) return;

        if (isViewportZoomed()) {
          hideMagnifier();
        }
      };

      window.visualViewport?.addEventListener('resize', handleViewportZoom);
      window.visualViewport?.addEventListener('scroll', handleViewportZoom);
      modal.addEventListener('gesturestart', hideMagnifier);
      modal.addEventListener('gesturechange', hideMagnifier);

      filterToggle.addEventListener('click', async () => {
        backgroundRevision += 1;
        colorFilterEnabled = !colorFilterEnabled;
        storeColorFilterPreference(colorFilterEnabled);
        syncFilterToggle();
        hideMagnifier();
        cancelRenders();
        filterToggle.disabled = true;

        try {
          await renderPdf({ crossFade: true });
          preloadOtherReports();
        } catch (error) {
          showFallback();
        } finally {
          filterToggle.disabled = false;
        }
      });

      let downloadPending = false;
      downloadButton.addEventListener('click', async (event) => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault();
        if (downloadPending) return;
        downloadPending = true;
        const url = activePdfUrl;
        const filename = reportDownloadFilename(modal.dataset.pdfLaboratory, modal.dataset.pdfReportNumber);
        let objectUrl;
        const link = document.createElement('a');
        link.download = filename;
        try {
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) throw new Error(`Unable to download PDF: ${response.status}`);
          objectUrl = URL.createObjectURL(await response.blob());
          link.href = objectUrl;
        } catch (error) {
          link.href = url;
        }
        document.body.appendChild(link);
        link.click();
        link.remove();
        if (objectUrl) window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
        downloadPending = false;
      });

      printButton.addEventListener('click', () => {
        const printUrl = activePdfUrl;

        printFrame.onload = () => {
          if (printFrame.src === 'about:blank') return;

          try {
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
          } catch (error) {
            window.open(printUrl, '_blank', 'noopener,noreferrer');
          }
        };

        printFrame.src = 'about:blank';
        window.requestAnimationFrame(() => {
          printFrame.src = printUrl;
        });
      });

      window.addEventListener('resize', () => {
        if (modal.hidden) {
          modal.dataset.rrPdfRenderKey = '';
          return;
        }

        hideMagnifier();
        cancelRenders();
        if (resizeFrame === null) {
          resizeFrame = window.requestAnimationFrame(() => {
            resizeFrame = null;
            if (modal.hidden) return;
            setHeaderOffset();
            fitDisplayedPages();
          });
        }
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => {
          if (modal.hidden) return;
          renderPdf().catch(error => {
            if (error?.name !== 'RenderingCancelledException') showFallback();
          });
        }, 180);
      });

      document.addEventListener('keydown', (event) => {
        if (!viewerOpen || modal.hidden || event.defaultPrevented || event.isComposing ||
            event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (event.target instanceof Element && event.target.closest(
          'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="slider"]'
        )) return;
        // Header drawers retain their own keyboard interactions above the viewer.
        if (document.querySelector('sidebar-menu[open], cart-drawer[open], #sidebar-menu[open], #cart-drawer[open]')) return;
        if (reportGroup().length < 2) return;
        event.preventDefault();
        switchReport(event.key === 'ArrowRight' ? 1 : -1);
      });

      modal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeModal();
          return;
        }

        if (
          event.key === 'ArrowUp' ||
          event.key === 'ArrowDown' ||
          event.key === 'PageUp' ||
          event.key === 'PageDown' ||
          event.key === 'Home' ||
          event.key === 'End' ||
          event.key === ' '
        ) {
          event.preventDefault();
          return;
        }

        if (event.key !== 'Tab') return;

        const focusable = Array.from(
          modal.querySelectorAll('a[href], button:not([disabled])')
        ).filter((element) => !element.hidden);

        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });

    };

    const initializeAllModals = (scope = document) => {
      scope.querySelectorAll(modalSelector).forEach(initializeModal);
    };

    if (!window.rrPdfModalDelegationReady) {
      window.rrPdfModalDelegationReady = true;

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest(triggerSelector);
        if (!trigger) return;

        const modalId = trigger.getAttribute('aria-controls');
        const modal = modalId ? document.getElementById(modalId) : null;
        const pdfUrl = trigger.dataset.rrPdfUrl || '';

        if (!modal) {
          if (trigger.tagName === 'A') return;
          if (pdfUrl) window.open(pdfUrl, '_blank', 'noopener,noreferrer');
          return;
        }

        event.preventDefault();
        initializeModal(modal);
        modal.rrOpenPdf?.(trigger, pdfUrl);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initializeAllModals(), { once: true });
    } else {
      initializeAllModals();
    }

    document.addEventListener('shopify:section:load', (event) => {
      initializeAllModals(event.target);
    });
  })();
